import React, { memo, useEffect, useState } from "react"
import { Box, Text } from "@chakra-ui/react"
import { Icon } from "@app/components/Icon"
import { useFunctions } from "@app/hooks/useFunctions"

type Props = {
  visibility?: boolean
  totalReviews?: number
  averageReview?: number
  productId?: string
  scrollToFunction?: () => void
}

export const ReviewOverview: React.FC<Props> = memo(({ totalReviews, averageReview, productId, visibility = true, scrollToFunction }) => {
  const { callFunction } = useFunctions()
  const [totalReviewsCount, setTotalReviewsCount] = useState<number | undefined>(totalReviews)
  const [averageReviewCount, setAverageReviewCount] = useState<number | undefined>(averageReview)
  useEffect(() => {
    // If we have the totalReviews and averageReview, we don't need to fetch them as these can be passed in
    if (averageReview !== undefined && totalReviews !== undefined) {
      setAverageReviewCount(averageReview)
      setTotalReviewsCount(totalReviews)
      return
    }
    if (!productId) return
    const fetchReviews = async () => {
      const {
        body: { response: bottomlineResponse },
      } = await callFunction("get-bottomline-reviews", { productId: productId })
      if (!bottomlineResponse) return
      setTotalReviewsCount(bottomlineResponse.bottomline.total_reviews)
      setAverageReviewCount(bottomlineResponse.bottomline.average_score)
    }
    fetchReviews()
  }, [averageReview, totalReviews, productId, callFunction])

  const flatReview = Math.floor(averageReviewCount || 0)
  const decimal = Math.abs((averageReviewCount || 0) - flatReview)
  const stars = Array.from({ length: 5 }, (_, index) => {
    if (flatReview === index) {
      if (decimal < 0.5) {
        return <Icon key={index} name={decimal >= 0.25 ? "review-star-half" : "review-star-empty"} width={5} height={5} />
      }
      return <Icon key={index} name={decimal >= 0.75 ? "review-star-full" : "review-star-half"} width={5} height={5} />
    }
    return <Icon key={index} name={flatReview >= index ? "review-star-full" : "review-star-empty"} width={5} height={5} />
  })

  return visibility && totalReviewsCount ? (
    <>
      <Box display={"flex"} onClick={scrollToFunction} cursor={"pointer"}>
        <Box display={"flex"} gap="1" alignItems={"start"} paddingRight={2}>
          {stars}
        </Box>
        <Text size="sm">{`(${totalReviewsCount})`}</Text>
      </Box>
    </>
  ) : null
})
